import { useEffect, useRef } from "react";

import { v4 as uuidV4 } from "uuid";

export const syncChannel = new BroadcastChannel("suno-tabs-sync-channel");

const sessionStorage = globalThis?.sessionStorage;

// Get or create appInstanceId from sessionStorage
// This persists across page refreshes within the same tab
// but creates a new ID for each new tab
const getOrCreateAppInstanceId = (): string => {
  const storageKey = "suno-app-instance-id";
  const existingId = sessionStorage?.getItem?.(storageKey);

  if (existingId) {
    console.log(
      "[AppInstanceId] Using existing ID from sessionStorage:",
      existingId
    );
    return existingId;
  }

  const newId = uuidV4();
  sessionStorage?.setItem?.(storageKey, newId);
  console.log(
    "[AppInstanceId] Created new ID and stored in sessionStorage:",
    newId
  );
  return newId;
};

export const appInstanceId = getOrCreateAppInstanceId();
const MAX_PROCESSED_MESSAGE_IDS = 3000;

export interface SyncMessage {
  emitterId?: string;
  action: string;
  payload?: any;
  messageId?: string;
}

export const dispatchTabSyncMessage = ({
  action,
  payload,
  messageId
}: SyncMessage) => {
  const newMessageId = messageId || uuidV4();
  syncChannel.postMessage(
    JSON.stringify({
      action,
      payload,
      emitterId: appInstanceId,
      messageId: newMessageId
    })
  );
};

export default function useSyncBetweenTabs<T extends SyncMessage>(
  processedMessageIds: React.MutableRefObject<Set<string>>,
  onMessage: (message: T) => void,
  skip: boolean = false
) {
  const processedOrderRef = useRef<string[]>([]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data && String(event.data).includes("{")) {
        try {
          const data = JSON.parse(event.data);
          const messageId = data.messageId;

          if (data.emitterId === appInstanceId) {
            return;
          }

          // Keep compatibility with legacy messages that may not include messageId.
          if (messageId) {
            if (processedMessageIds.current.has(messageId)) {
              return;
            }

            processedMessageIds.current.add(messageId);
            processedOrderRef.current.push(messageId);

            // Prevent unbounded growth of dedupe tracking in long-lived tabs.
            while (processedOrderRef.current.length > MAX_PROCESSED_MESSAGE_IDS) {
              const oldestId = processedOrderRef.current.shift();
              if (oldestId) {
                processedMessageIds.current.delete(oldestId);
              }
            }
          }

          console.log("Received a message from a tab", {
            data,
            currentAppInstanceId: appInstanceId
          });
          onMessage(data as T);
        } catch {}
      }
    };

    if (!skip) {
      syncChannel.addEventListener("message", handleMessage);
    }

    return () => {
      if (!skip) {
        syncChannel.removeEventListener("message", handleMessage);
      }
    };
  }, [onMessage, processedMessageIds, skip]);
}
